import { distance, point } from "@turf/turf";
import { notification } from "antd";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { Coords } from "tm-shared/types";

import { BaseModal } from "@/components/BaseModal";
import { useAppDispatch } from "@/hooks/redux";
import { useMakeCommand } from "@/hooks/useRequestQueue";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { addModalToQueue } from "@/store/slices/ui";
import { setTaskCompleted } from "@/store/slices/work";
import { ModalPayloads, ModalType } from "@/types/modals";
import { getPreciseLocation, parseMarketCoords } from "@/utils/geo/geo";

interface StartVisitModalProps {
    onCloseModal: () => void;
    payload: ModalPayloads[ModalType.StartVisit];
}

export const StartVisitModal: FC<StartVisitModalProps> = ({
    payload,
    onCloseModal,
}) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const cancelledRef = useRef(false);

    const { makeTaskCompleteCommands } = useMakeCommand();

    const { market, taskId } = payload;

    const marketCoords = useMemo(
        () => parseMarketCoords(market?.geo ?? null),
        [market?.geo],
    );

    const handleTaskComplete = useCallback(
        async (coords: Coords | null = null) => {
            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload: { coords },
                },
                marketId: market.id,
                coords,
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        marketId: market.id,
                        taskId,
                    },
                    commands: taskCompleteCommands,
                }),
            );

            dispatch(
                setTaskCompleted({
                    marketId: market.id,
                    taskId,
                }),
            );

            onCloseModal();
        },
        [dispatch, makeTaskCompleteCommands, market.id, onCloseModal, taskId],
    );

    const handleConfirm = async () => {
        cancelledRef.current = false;
        setLoading(true);

        try {
            const coords = await getPreciseLocation();

            if (cancelledRef.current) return;

            if (marketCoords) {
                const myPoint = point([coords.lng, coords.lat]);
                const marketPoint = point(marketCoords);

                const dist = distance(myPoint, marketPoint, {
                    units: "kilometers",
                });
                const distRounded = Math.round(dist * 10) / 10;

                if (distRounded > 10) {
                    dispatch(
                        addModalToQueue({
                            type: ModalType.StartVisitWarning,
                            id: `${ModalType.StartVisitWarning}-${payload.taskId}`,
                            payload: {
                                ...payload,
                                dist: distRounded,
                                coords,
                            },
                        }),
                    );
                    onCloseModal();
                    return;
                }
            }

            handleTaskComplete(coords);
        } catch (error) {
            if (cancelledRef.current) return;

            const geoErr = error as GeolocationPositionError;
            if (geoErr && typeof geoErr === "object" && "code" in geoErr) {
                if (geoErr.code === 1) {
                    notification.error({
                        message:
                            "Не вдалося отримати вашу геолокацію. Дозвольте доступ та спробуйте ще раз.",
                    });

                    return;
                }
            }

            handleTaskComplete();
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        cancelledRef.current = true;
        onCloseModal();
    };

    return (
        <BaseModal
            open
            onClose={handleClose}
            onConfirm={handleConfirm}
            title="Розпочати візит"
            description="Після підтвердження початку роботи у вибраній торговій точці розпочнеться відлік робочого часу."
            confirmText="Розпочати"
            cancelText="Скасувати"
            confirmLoading={loading}
            maskClosable={!loading}
        />
    );
};
